/* import __COLOCATED_TEMPLATE__ from './settings.hbs'; */
/* RESPONSIBLE TEAM: team-workflows */

import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Wait from 'embercom/models/operator/visual-builder/step/wait';

interface Arguments {
  step: Wait;
}

export default class Settings extends Component<Arguments> {
  @service intl!: IntlService;

  @tracked durationType = 'custom';
  @tracked until = 'later_today';
  @tracked interruptMode = 'admin_comments';
  @tracked customDuration: number | undefined;

  constructor(owner: unknown, args: Arguments) {
    super(owner, args);
    this._populateOptions();
  }

  _populateOptions() {
    if (this.args.step.durationType === 'custom') {
      this.durationType = 'custom';
    } else {
      this.durationType = 'until';
      this.until = this.args.step.durationType;
    }

    this.interruptMode = this.args.step.interruptMode;
    this.customDuration = this.args.step.customDuration;
  }

  get untilLabel() {
    if (this.durationType === 'custom') {
      return this.intl.t('operator.workflows.visual-builder.wait.until-options.later-today.text');
    } else {
      return this.intl.t(
        `operator.workflows.visual-builder.wait.until-options.${this.until.replace('_', '-')}.text`,
      );
    }
  }

  get untilGroupList() {
    return [
      {
        items: [
          {
            text: this.intl.t(
              'operator.workflows.visual-builder.wait.until-options.later-today.text',
            ),
            value: 'later_today',
            description: this.intl.t(
              'operator.workflows.visual-builder.wait.until-options.later-today.description',
            ),
            component: 'workflows/graph-editor/node-items/steps/select-items/duration-item',
          },
          {
            text: this.intl.t('operator.workflows.visual-builder.wait.until-options.tomorrow.text'),
            value: 'tomorrow',
            description: this.intl.t(
              'operator.workflows.visual-builder.wait.until-options.tomorrow.description',
            ),
            component: 'workflows/graph-editor/node-items/steps/select-items/duration-item',
          },
          {
            text: this.intl.t('operator.workflows.visual-builder.wait.until-options.monday.text'),
            value: 'monday',
            description: this.intl.t(
              'operator.workflows.visual-builder.wait.until-options.monday.description',
            ),
            component: 'workflows/graph-editor/node-items/steps/select-items/duration-item',
          },
        ],
      },
    ];
  }

  @action
  selectUntil() {
    this.durationType = 'until';
    this.args.step.durationType = this.until;
    this.args.step.customDuration = undefined;
  }

  @action
  updateCustomDuration(durationInSeconds: number) {
    this.customDuration = durationInSeconds;
    this.setCustomDuration();
  }

  @action
  setCustomDuration() {
    this.durationType = 'custom';
    this.args.step.durationType = 'custom';
    this.args.step.customDuration = this.customDuration;
  }

  @action
  updateUntil(selectedValue: string) {
    this.durationType = 'until';
    this.until = selectedValue;
    this.args.step.durationType = this.until;
    this.args.step.customDuration = undefined;
  }

  get interruptModeLabel() {
    return this.intl.t(
      `operator.workflows.visual-builder.wait.interrupt-mode-options.${this.interruptMode.replaceAll(
        '_',
        '-',
      )}`,
    );
  }

  get interruptModeGroupList() {
    return [
      {
        items: [
          {
            text: this.intl.t(
              `operator.workflows.visual-builder.wait.interrupt-mode-options.admin-comments`,
            ),
            value: 'admin_comments',
          },
          {
            text: this.intl.t(
              `operator.workflows.visual-builder.wait.interrupt-mode-options.admin-and-user-comments`,
            ),
            value: 'admin_and_user_comments',
          },
        ],
      },
    ];
  }

  @action
  selectInterruptMode(selectedValue: string): void {
    this.interruptMode = selectedValue;
    this.args.step.interruptMode = selectedValue;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::Wait::Settings': typeof Settings;
    'workflows/graph-editor/node-items/steps/wait/settings': typeof Settings;
  }
}
